import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button } from "@mui/material";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import {
  loadApplicableHolidaysFromBackend,
  loadOptionalHolidaysFromBackend,
  electHolidayInBackend,
} from "app/services/holiday.service";
import { handleAxiosError } from "app/services/app.error.handler";

// Shown inside the Add Leave dialog for the chosen date range:
// - PUBLIC/company holidays -> informational hint (leave likely not needed).
// - OPTIONAL holidays -> if the member still has quota, offer to avail it (an election) instead of
//   spending leave; if the quota is used up, tell them to apply it as a normal leave.
export const HolidayDateNudge = ({ startDate, endDate, ownerId, isMember, onAvailed }) => {
  const [applicable, setApplicable] = useState([]); // [{ date, name, description, holidayType }]
  const [optionalSummary, setOptionalSummary] = useState(null); // { cap, used, remaining, holidays: [] }
  const [busyId, setBusyId] = useState(null);

  // Company-wide holidays in range (public + optional); no member context needed.
  useEffect(() => {
    if (!startDate || !endDate) {
      setApplicable([]);
      return;
    }
    let active = true;
    loadApplicableHolidaysFromBackend(startDate, endDate)
      .then((res) => {
        if (active) setApplicable(res.status === 204 ? [] : res.data || []);
      })
      .catch(() => {
        if (active) setApplicable([]); // best-effort hint; never block the dialog
      });
    return () => {
      active = false;
    };
  }, [startDate, endDate]);

  // Owner-scoped optional-holiday election data (ids + quota) for the avail action.
  // ownerId is the member's or manager's own id, depending on isMember.
  useEffect(() => {
    if (ownerId == null || !startDate) {
      setOptionalSummary(null);
      return;
    }
    let active = true;
    loadOptionalHolidaysFromBackend(ownerId, isMember, dayjs(startDate).year())
      .then((res) => {
        if (active) setOptionalSummary(res.status === 204 ? null : res.data);
      })
      .catch(() => {
        if (active) setOptionalSummary(null);
      });
    return () => {
      active = false;
    };
  }, [ownerId, isMember, startDate]);

  if (!startDate || !endDate) return null;

  const inRange = (d) =>
    !dayjs(d).isBefore(dayjs(startDate), "day") && !dayjs(d).isAfter(dayjs(endDate), "day");

  const publicHolidays = (applicable || []).filter(
    (h) => h.holidayType !== "OPTIONAL" && inRange(h.date)
  );
  const availableOptional =
    optionalSummary && optionalSummary.holidays
      ? optionalSummary.holidays.filter((h) => !h.elected && inRange(h.date))
      : [];

  if (publicHolidays.length === 0 && availableOptional.length === 0) return null;

  const avail = async (holiday) => {
    setBusyId(holiday.holidayId);
    await electHolidayInBackend(holiday.holidayId, ownerId, isMember)
      .then(() => {
        if (onAvailed) onAvailed(holiday);
      })
      .catch((error) => handleAxiosError(error));
    setBusyId(null);
  };

  return (
    <MDBox my={1}>
      {publicHolidays.length > 0 && (
        <Alert severity="info" sx={{ mb: availableOptional.length > 0 ? 1 : 0 }}>
          {`${publicHolidays.map((h) => `${h.date} (${h.name})`).join(", ")} ${
            publicHolidays.length > 1 ? "are company holidays" : "is already a company holiday"
          } — leave may not be needed for ${publicHolidays.length > 1 ? "those days" : "that day"}.`}
        </Alert>
      )}

      {availableOptional.length > 0 &&
        (optionalSummary.remaining <= 0 ? (
          <Alert severity="info">
            {`${availableOptional.map((h) => `${h.date} (${h.name})`).join(", ")} ${
              availableOptional.length > 1 ? "are optional holidays" : "is an optional holiday"
            }. You have already availed your optional holiday quota. Please apply it as a normal leave.`}
          </Alert>
        ) : (
          availableOptional.map((holiday) => (
            <Alert
              key={holiday.holidayId}
              severity="warning"
              sx={{ mb: 1 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={busyId === holiday.holidayId}
                  onClick={() => avail(holiday)}
                >
                  Avail Optional Holiday
                </Button>
              }
            >
              {`${holiday.date} (${holiday.name}) is an optional holiday. Avail it as an optional holiday instead of using leave? (${optionalSummary.remaining} of ${optionalSummary.cap} remaining)`}
            </Alert>
          ))
        ))}
    </MDBox>
  );
};

HolidayDateNudge.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isMember: PropTypes.bool,
  onAvailed: PropTypes.func,
};
